import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';

export interface IMediaItem {
  id: number;
  file_name: string;
  path: string;
  mime_type?: string;
  size?: number;
}

export interface ISop {
  id?: number | string;
  tenant_id?: number | string;
  name?: string;
  org_chart_note_id?: number;
  org_chart_node_id?: number;
  org_chart_node?: {
    id: number;
    name: string;
  };
  org_job_description_id?: number | string;
  sop_file?: string;
  media?: IMediaItem[];
  sop?: Array<{ man?: string; machine?: string; material?: string; method?: string; [key: string]: unknown }>;
  org_sop?: Array<{ man?: string; machine?: string; material?: string; method?: string; [key: string]: unknown }>;
  created_at?: string;
  updated_at?: string;
  is_published?: boolean;
  published?: boolean;
}

interface SopState {
  sops: ISop[];
  paginatedData: {
    content: ISop[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
  } | null;
  selectedSop: ISop | null;
  isLoading: boolean;
  error: unknown;

  createSop: (tenantId: string | number, payload: Partial<ISop>) => Promise<ISop>;
  getSops: (
    tenantId: string | number,
    params?: {
      page?: number;
      page_size?: number;
      org_chart_node_id?: number;
      search?: string;
      published?: boolean;
      order?: string;
      order_by?: string;
    },
  ) => Promise<void>;
  getSopDetail: (tenantId: string | number, id: number | string) => Promise<ISop | void>;
  updateSop: (tenantId: string | number, id: number | string, payload: Partial<ISop>) => Promise<void>;
  deleteSop: (tenantId: string | number, id: number | string) => Promise<void>;
  bulkPublishSop: (tenantId: string | number, isPublished: boolean) => Promise<void>;

  clearSops: () => void;
  setSops: (data: ISop[]) => void;
}

const useSopState = create<SopState>((set, get) => ({
  sops: [],
  paginatedData: null,
  selectedSop: null,
  isLoading: false,
  error: null,

  setSops: data => set({ sops: data }),
  clearSops: () => set({ sops: [] }),

  createSop: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/sop/${tenantId}`;
      const { data } = await axiosInstance.post(url, payload);

      toast.success('SOP berhasil dibuat');
      set({ isLoading: false });
      return data.data || data;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
      throw error;
    }
  },

  getSops: async (tenantId, params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/sop/${tenantId}`;
      const { data } = await axiosInstance.get(url, { params });

      const responseData = data.data;
      // Normalize ID field: Go's gorm.Model may serialize primary key as "ID" (uppercase).
      // Map it to lowercase "id" so frontend can reliably use row.id for edit/delete.
      const rawContent: Array<Record<string, unknown>> = responseData?.content || [];
      const content: ISop[] = rawContent.map(item => ({
        ...item,
        id: (item.id ?? item.ID) as number | string | undefined,
      }));
      set({
        sops: content,
        paginatedData: {
          content,
          totalElements: responseData?.totalElements || 0,
          totalPages: responseData?.totalPages || 0,
          size: responseData?.size || 10,
          number: responseData?.number || 0,
        },
        isLoading: false,
      });
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  getSopDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/sop/${tenantId}/${id}`;
      const { data } = await axiosInstance.get(url);

      set({ selectedSop: data.data, isLoading: false });
      return data;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  updateSop: async (tenantId, id, payload) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/sop/${tenantId}/${id}`;
      const { data } = await axiosInstance.put(url, payload);

      set(state => ({
        sops: state.sops.map(sop => (sop.id === id ? { ...sop, ...data.data } : sop)),
        selectedSop: data.data,
        isLoading: false,
      }));

      toast.success('SOP berhasil diperbarui');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  deleteSop: async (tenantId, id) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/sop/${tenantId}/${id}`;
      await axiosInstance.delete(url);

      set(state => ({
        sops: state.sops.filter(sop => sop.id !== id),
        isLoading: false,
      }));

      toast.success('SOP berhasil dihapus');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  bulkPublishSop: async (tenantId: string | number, isPublished: boolean) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/sop/${tenantId}/bulk-publish`;
      const { data } = await axiosInstance.put(url, { published: isPublished });

      set(state => ({
        sops: state.sops.map(ka => ({
          ...ka,
          published: isPublished,
        })),
        isLoading: false,
      }));

      toast.success(isPublished ? 'Semua SOP berhasil dipublikasikan' : 'Semua SOP berhasil dibatalkan publikasinya');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },
}));

export default useSopState;
